/**
 * Moderation store — user account & messaging statuses.
 *
 * Source of truth: localStorage user records (managed by `lib/storage.js`).
 * Every mutation also mirrors to Supabase `profiles` table (see SQL below).
 *
 * Status fields on the user/profile record:
 *   - account_status:           "active" | "suspended" | "banned"
 *   - suspension_reason:        string | null
 *   - suspended_until:          ISO date string | null  (null = indefinite)
 *   - ban_reason:               string | null
 *   - banned_at:                ISO date string | null
 *   - messaging_status:         "active" | "suspended"
 *   - messaging_suspension_reason: string | null
 *
 * Required Supabase SQL (run once on the `profiles` table):
 *
 *   ALTER TABLE profiles ADD COLUMN IF NOT EXISTS account_status TEXT DEFAULT 'active';
 *   ALTER TABLE profiles ADD COLUMN IF NOT EXISTS suspension_reason TEXT;
 *   ALTER TABLE profiles ADD COLUMN IF NOT EXISTS suspended_until TIMESTAMPTZ;
 *   ALTER TABLE profiles ADD COLUMN IF NOT EXISTS ban_reason TEXT;
 *   ALTER TABLE profiles ADD COLUMN IF NOT EXISTS banned_at TIMESTAMPTZ;
 *   ALTER TABLE profiles ADD COLUMN IF NOT EXISTS messaging_status TEXT DEFAULT 'active';
 *   ALTER TABLE profiles ADD COLUMN IF NOT EXISTS messaging_suspension_reason TEXT;
 *
 * If the columns don't exist yet, Supabase mirroring will fail silently
 * (offline-first localStorage stays the source of truth).
 */
import { getUsers, upsertUser, getSessionUser } from "@/lib/storage";
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";

export const ACCOUNT_STATUS = {
    ACTIVE: "active",
    SUSPENDED: "suspended",
    BANNED: "banned",
};

export const MESSAGING_STATUS = {
    ACTIVE: "active",
    SUSPENDED: "suspended",
};

const fireMirror = (row) => {
    try { mirror(SUPABASE_TABLES.PROFILES, row); } catch { /* ignore */ }
};

const findUser = (userId) => getUsers().find((u) => u.id === userId) || null;

/** Live status — auto-expires suspensions whose `suspended_until` has passed. */
export const getEffectiveStatus = (user) => {
    if (!user) return ACCOUNT_STATUS.ACTIVE;
    const status = user.account_status || ACCOUNT_STATUS.ACTIVE;
    if (status === ACCOUNT_STATUS.SUSPENDED && user.suspended_until) {
        const until = Date.parse(user.suspended_until);
        if (Number.isFinite(until) && until < Date.now()) {
            return ACCOUNT_STATUS.ACTIVE; // expired — treated as active
        }
    }
    return status;
};

export const isBanned = (user) => getEffectiveStatus(user) === ACCOUNT_STATUS.BANNED;
export const isSuspended = (user) => getEffectiveStatus(user) === ACCOUNT_STATUS.SUSPENDED;
export const isMessagingSuspended = (user) =>
    (user?.messaging_status || MESSAGING_STATUS.ACTIVE) === MESSAGING_STATUS.SUSPENDED;

export const suspendUser = (userId, { reason, durationDays = null } = {}) => {
    const u = findUser(userId);
    if (!u) return null;
    const until = durationDays
        ? new Date(Date.now() + Number(durationDays) * 24 * 3600 * 1000).toISOString()
        : null;
    const next = {
        ...u,
        account_status: ACCOUNT_STATUS.SUSPENDED,
        suspension_reason: reason || null,
        suspended_until: until,
        ban_reason: null,
        banned_at: null,
    };
    upsertUser(next);
    fireMirror({
        id: u.id,
        account_status: next.account_status,
        suspension_reason: next.suspension_reason,
        suspended_until: next.suspended_until,
        ban_reason: null,
        banned_at: null,
    });
    return next;
};

export const banUser = (userId, { reason } = {}) => {
    const u = findUser(userId);
    if (!u) return null;
    const next = {
        ...u,
        account_status: ACCOUNT_STATUS.BANNED,
        ban_reason: reason || null,
        banned_at: new Date().toISOString(),
        suspension_reason: null,
        suspended_until: null,
    };
    upsertUser(next);
    fireMirror({
        id: u.id,
        account_status: next.account_status,
        ban_reason: next.ban_reason,
        banned_at: next.banned_at,
        suspension_reason: null,
        suspended_until: null,
    });
    return next;
};

export const reinstateUser = (userId) => {
    const u = findUser(userId);
    if (!u) return null;
    const next = {
        ...u,
        account_status: ACCOUNT_STATUS.ACTIVE,
        suspension_reason: null,
        suspended_until: null,
        ban_reason: null,
        banned_at: null,
    };
    upsertUser(next);
    fireMirror({
        id: u.id,
        account_status: ACCOUNT_STATUS.ACTIVE,
        suspension_reason: null,
        suspended_until: null,
        ban_reason: null,
        banned_at: null,
    });
    return next;
};

export const suspendMessaging = (userId, { reason } = {}) => {
    const u = findUser(userId);
    if (!u) return null;
    const next = {
        ...u,
        messaging_status: MESSAGING_STATUS.SUSPENDED,
        messaging_suspension_reason: reason || null,
    };
    upsertUser(next);
    fireMirror({
        id: u.id,
        messaging_status: next.messaging_status,
        messaging_suspension_reason: next.messaging_suspension_reason,
    });
    return next;
};

export const restoreMessaging = (userId) => {
    const u = findUser(userId);
    if (!u) return null;
    const next = {
        ...u,
        messaging_status: MESSAGING_STATUS.ACTIVE,
        messaging_suspension_reason: null,
    };
    upsertUser(next);
    fireMirror({
        id: u.id,
        messaging_status: MESSAGING_STATUS.ACTIVE,
        messaging_suspension_reason: null,
    });
    return next;
};

/** Live convenience for components — returns a freshly-read view of the
 *  currently signed-in user's moderation flags. */
export const getCurrentUserStatus = () => {
    const u = getSessionUser();
    if (!u) return null;
    return {
        userId: u.id,
        status: getEffectiveStatus(u),
        suspensionReason: u.suspension_reason || null,
        suspendedUntil: u.suspended_until || null,
        banReason: u.ban_reason || null,
        bannedAt: u.banned_at || null,
        messagingStatus: u.messaging_status || MESSAGING_STATUS.ACTIVE,
        messagingSuspensionReason: u.messaging_suspension_reason || null,
    };
};
